<template>
    <Dialog
        class="part-recognition-dialog"
        :class="{ landscape: isLandscapeAndLtMd }"
        :visible="isDialogVisible"
        :title="$t('shared.AI Part Recognition')"
        :maximized="$q.platform.is.mobile && $q.screen.lt.md"
        :position="dialogPosition"
        @on-close="onDialogClose"
    >
        <template #content>
            <div v-if="isUploading" class="uploading-box column">
                <div class="q-mb-md">{{ $t("shared.Image is uploading") }}</div>
                <q-linear-progress size="25px" :value="uploadProgressValue" color="accent">
                    <div class="absolute-full flex flex-center">
                        <q-badge color="white" text-color="accent" :label="`${uploadedPercent}%`" />
                    </div>
                </q-linear-progress>
                <QitButton
                    v-if="isCancelUploadVisible"
                    class="q-mt-lg"
                    :label="$t('shared.Cancel')"
                    @click="onCancelUpload"
                />
            </div>
            <template v-else>
                <input
                    ref="inputRef"
                    type="file"
                    class="hidden"
                    :accept="acceptedFileExtensions"
                    @change="onImageSelect"
                />
                <input
                    v-if="captureIsSupported"
                    ref="fileInputRef"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    class="hidden"
                    @change="onNativeCaptureImage"
                />
                <template v-if="dialogState === 'init'">
                    <InitialContent
                        :compact-view="dialogPosition === 'bottom'"
                        @on-open-camera="openCameraDialog"
                        @on-open-file-explorer="() => inputRef?.click()"
                        @on-file-dragged="setSelectedImageFile"
                    />
                </template>
                <template v-if="dialogState === 'crop'">
                    <Cropper
                        v-if="imageState.base64 && imageState.mode"
                        :compact-view="isLandscapeAndLtMd"
                        :image="imageState.base64"
                        :mode="imageState.mode"
                        @on-search="onSearch"
                        @on-crop-change="onCroppingImage"
                        @on-renew="onRenew"
                        @on-reset="onResetCropper"
                    />
                </template>
                <template v-if="dialogState === 'image'">
                    <ImageViewer
                        v-if="imageToSearchBase64 && imageState.mode"
                        :compact-view="isLandscapeAndLtMd"
                        :image-src="imageToSearchBase64"
                        :mode="imageState.mode"
                        @on-search="onSearch"
                        @on-renew="onRenew"
                        @on-crop="onCropImage"
                    />
                </template>
            </template>
        </template>
    </Dialog>
    <CameraDialog
        v-if="!captureIsSupported"
        :title="$t('shared.AI Part Recognition')"
        :visible="isCameraVisible"
        @on-close="closeCameraDialog"
        @on-capture-image="onCaptureImage"
    />
</template>

<script setup lang="ts">
import Cropper from "@/shared/camera/cropper.vue";
import ImageViewer from "@/shared/part-recognition/dialog/image-viewer.vue";
import InitialContent from "@/shared/part-recognition//dialog/initial-content.vue";
import Dialog from "@/shared/components/dialog/dialog.vue";
import QitButton from "@/shared/components/buttons/qit-button.vue";
import { computed, onMounted, reactive, ref, useTemplateRef } from "vue";
import CameraDialog from "@/shared/camera/camera-dialog.vue";
import { watchImmediate } from "@vueuse/core";
import { UploadOptions, useUploader } from "@/shared/components/file-uploader/uploader.ts";
import { usePartsSearchConfigQuery } from "@/shared/services/graphql/generated/consumer-graph-types.ts";
import { uid, useQuasar } from "quasar";
import { useI18n } from "vue-i18n";
import { convertBytesToDisplayText } from "@/shared/utils/byte-formatter.ts";
import { UploadBlobResult } from "@/shared/environment/blob/blob.types.ts";
import { scaleImageToBlob } from "@/shared/part-recognition/part-recognition.utils.ts";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";

export type ImageMode = "captured" | "selected";

interface CroppedImage {
    file?: File;
    base64?: string;
}

export interface PartRecognitionImage {
    file: File;
    base64: string;
    blobResult: UploadBlobResult;
    mode?: ImageMode;
}

interface ImageState {
    file?: File;
    base64?: string;
    cropped: CroppedImage;
    mode?: ImageMode;
}

export interface ScaleOptions {
    maxResolution?: number;
    maxDimensions?: {
        /**
         * Maximum allowed length of the longest side of the image (width or height, depending on orientation).
         */
        maxSideLength: number;
        /**
         * Maximum allowed length of the shorter side of the image.
         */
        shortSideLength: number;
    };
}

const DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024;
const DEFAULT_MIME_TYPES = ["image/jpeg", "image/png"];
const DEFAULT_FILE_EXTENSIONS = ["jpg", "png"];
const UPLOAD_URL = "/api/upload/search-image";

const props = defineProps<{ visible: boolean; image?: PartRecognitionImage }>();
const emits = defineEmits<{ onClose: []; onSearch: [partRecognitionImage: PartRecognitionImage] }>();

const inputRef = useTemplateRef<HTMLInputElement>("inputRef");
const fileInput = useTemplateRef<HTMLInputElement>("fileInputRef");
const dialogState = ref<"init" | "camera" | "crop" | "image">("init");
const isDialogVisible = ref<boolean>(false);
const isCameraVisible = ref<boolean>(false);
const uploadedPercent = ref<number>(0);

const { screen } = useQuasar();

const isLandscapeAndLtMd = computed(() => {
    return screen.lt.md && isLandscape.value;
});

const imageState = reactive<ImageState>({
    file: undefined,
    base64: undefined,
    cropped: {
        file: undefined,
        base64: undefined,
    },
    mode: undefined,
});

const { t } = useI18n();
const { notify } = useQuasar();
const { result } = usePartsSearchConfigQuery();
const { uploadBlob, abort, isUploading } = useUploader();
const { isXSmall, isLandscape } = useScreenSize();
const captureIsSupported = ref(false);

const { hasAccess } = useHasAccess({ featureID: AccessFeature.parts_search_low_res });

onMounted(() => {
    let captureInputElement: HTMLInputElement | undefined;
    try {
        captureInputElement = document.createElement("input");
        captureIsSupported.value = captureInputElement.capture != undefined;
    } finally {
        captureInputElement?.remove();
    }
});

watchImmediate(
    () => props.visible,
    (newVal) => {
        isDialogVisible.value = newVal;
        if (newVal && props.image) {
            dialogState.value = "image";
            Object.assign(imageState, props.image);
        }
    }
);

const dialogPosition = computed(() => {
    return (isXSmall.value || isLandscapeAndLtMd.value) && dialogState.value === "init" ? "bottom" : "standard";
});

const maxUploadFileSize = computed(
    () => result.value?.tenant.uploadConfig?.aiPartsSearch?.image?.limits.maxFileSize || DEFAULT_MAX_FILE_SIZE
);

const allowedMimeTypes = computed(
    () => result.value?.tenant.uploadConfig?.aiPartsSearch?.image?.allowedMimeTypes || DEFAULT_MIME_TYPES
);

const allowedFileExtensions = computed(
    () => result.value?.tenant.uploadConfig?.aiPartsSearch?.image?.allowedFileExtensions || DEFAULT_FILE_EXTENSIONS
);

const acceptedFileExtensions = computed(() => allowedFileExtensions.value.map((x) => `.${x}`).join(", "));

const imageToSearchBase64 = computed(() => imageState.cropped.base64 || imageState.base64);

const imageFileToSearch = computed(() => imageState.cropped.file || imageState.file);

const resetImageState = () => {
    // Replacing the whole object (e.g. imageState = {...}) would break the reactivity link.
    Object.assign(imageState, {
        file: undefined,
        base64: undefined,
        dataUrl: undefined,
        mode: undefined,
        cropped: { file: undefined, base64: undefined },
    });
};

const onDialogClose = () => {
    isDialogVisible.value = false;
    resetImageState();
    dialogState.value = "init";
    emits("onClose");
};

const openCameraDialog = () => {
    if (captureIsSupported.value && fileInput.value) {
        fileInput.value.click();
        return;
    }

    isCameraVisible.value = true;
    isDialogVisible.value = false;
    dialogState.value = "camera";
};

const closeCameraDialog = () => {
    isCameraVisible.value = false;
    dialogState.value = "init";
    emits("onClose");
};

const isFileValid = (file: File) => {
    const fileNameParts = file.name.split(".");
    const fileExtension = fileNameParts[fileNameParts.length - 1];

    return (
        allowedFileExtensions.value
            .filter((x): x is string => x !== null)
            .map((x: string) => x.toLowerCase())
            .includes(fileExtension.toLowerCase()) && allowedMimeTypes.value.includes(file.type)
    );
};

const validateFile = (file: File) => {
    if (!isFileValid(file)) {
        notify({
            message: `${t("shared.Upload one of the following file types")}: ${allowedFileExtensions.value.join(", ")}`,
            type: "negative",
        });

        return false;
    }

    return true;
};

const onImageSelect = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files[0]) return;

    resetImageState();
    const file = input.files[0];
    if (!validateFile(file)) return;

    setSelectedImageFile(file);
};

const setSelectedImageFile = (file: File) => {
    resetImageState();

    if (!validateFile(file)) return;

    try {
        const img = new Image();
        const reader = new FileReader();

        reader.onloadend = () => {
            img.src = reader.result as string;
            imageState.file = file;
            imageState.base64 = reader.result as string;
            imageState.mode = "selected";
            dialogState.value = "image";
        };

        reader.readAsDataURL(file);
    } catch (e) {
        console.error(e);
    }
};

const convertBlobToFile = (blob: Blob) => {
    return new File([blob], `${uid()}.jpg`, { type: "image/jpeg" });
};

const onCaptureImage = async (blob: Blob) => {
    resetImageState();

    const reader = new FileReader();
    reader.onloadend = async () => {
        imageState.base64 = reader.result as string;
    };
    reader.readAsDataURL(blob);

    imageState.file = convertBlobToFile(blob);
    imageState.mode = "captured";
    isDialogVisible.value = true;
    isCameraVisible.value = false;
    dialogState.value = "image";
};

const onNativeCaptureImage = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files ? input.files[0] : undefined;
    if (!file) return;

    resetImageState();

    const reader = new FileReader();
    reader.onloadend = async () => {
        imageState.base64 = reader.result as string;
    };
    reader.readAsDataURL(file);

    imageState.file = file;
    imageState.mode = "captured";
    isDialogVisible.value = true;
    isCameraVisible.value = false;
    dialogState.value = "image";
};

const onCropImage = () => {
    dialogState.value = "crop";
};

const onCroppingImage = (blob: Blob) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
        imageState.cropped.base64 = reader.result as string;
    };
    reader.readAsDataURL(blob);
    imageState.cropped.file = convertBlobToFile(blob);
};

const onResetCropper = () => {
    imageState.cropped.file = undefined;
    imageState.cropped.base64 = undefined;
    dialogState.value = "image";
};

const uploadProgressValue = computed(() => {
    if (uploadedPercent.value) return uploadedPercent.value / 100;

    return 0;
});

const isCancelUploadVisible = computed(() => {
    return uploadedPercent.value !== 100;
});

const scaleOptions = computed<ScaleOptions>(() => {
    return hasAccess.value
        ? { maxDimensions: { maxSideLength: 5096, shortSideLength: 690 } }
        : { maxResolution: 2_000_000 };
});

//Upload image and emit search event with payload
const onSearch = async () => {
    if (!imageFileToSearch.value) return;

    const scaledBlob = await scaleImageToBlob(imageFileToSearch.value, scaleOptions.value);
    if (!scaledBlob) return;

    const scaledFile = convertBlobToFile(scaledBlob);

    if (scaledFile.size > maxUploadFileSize.value) {
        notify({
            message: `${t("shared.Image is too large. Maximum size is")} ${convertBytesToDisplayText(
                maxUploadFileSize.value,
                0
            )}.`,
            type: "negative",
        });

        return;
    }

    const formData = new FormData();
    formData.append("file", scaledFile);

    try {
        const uploadOptions: UploadOptions = {
            url: UPLOAD_URL,
            formData,
            onProgress: (_loaded, _total, percent) => {
                uploadedPercent.value = percent;
            },
        };

        const result = await uploadBlob(uploadOptions);
        const partRecognitionImage: PartRecognitionImage = {
            base64: imageToSearchBase64.value as string,
            file: imageFileToSearch.value,
            blobResult: result,
            mode: imageState.mode,
        };
        emits("onSearch", partRecognitionImage);
        onDialogClose();
    } catch (e) {
        console.error(e);
        notify({ message: t("shared.Image upload failed"), type: "negative" });
    } finally {
        uploadedPercent.value = 0;
    }
};

const onRenew = () => {
    if (imageState.mode === "selected") {
        inputRef.value?.click();
    } else if (imageState.mode === "captured") {
        openCameraDialog();
    }
};

const onCancelUpload = () => {
    abort();
    uploadedPercent.value = 0;
};
</script>

<style lang="scss" scoped>
.uploading-box {
    width: 100%;
    height: 300px;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>

<style lang="scss">
.part-recognition-dialog {
    &.landscape {
        .q-card__section {
            padding-top: 0;
        }
        .cropper,
        .cropper-box {
            height: 100%;
            max-height: 100%;
            max-width: 60dvw !important;
        }
    }
    .dialog-card {
        flex-wrap: nowrap;
        max-width: 815px !important;
        width: 815px !important;
        overflow: hidden;
    }
    .cropper-box,
    .cropper {
        max-width: 100%;
        max-height: 50dvh;
        .screen--xs & {
            max-height: 45dvh;
        }
    }
    .loading-box {
        height: 50dvh;
    }
    .image-box {
        .screen--xs & {
            display: flex;
            flex: 1;
        }
        max-height: 50dvh;
        height: 50dvh;

        .q-img {
            height: 100%;
            max-height: 100%;

            .screen--xl &,
            .screen--lg & {
                max-height: 60dvh;
                height: 60dvh;
            }
        }
        .screen--xl &,
        .screen--lg & {
            max-height: 60dvh;
            height: 60dvh;
        }
    }
    .q-card__section {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    .buttons {
        display: flex;
        flex-direction: column;
        gap: 16px;
        justify-content: flex-end;
        flex-grow: 1;
    }
}
</style>
